import ExcelJS from 'exceljs';

export interface ExcelParseOptions {
    /**
     * Sheet index to parse (0-based)
     * @default 0
     */
    sheetIndex?: number;
    /**
     * Sheet name to parse (takes priority over sheetIndex)
     */
    sheetName?: string;
    /**
     * If true, automatically downloads the JSON
     * @default false
     */
    autoDownload?: boolean;
    /**
     * Download file name (without extension)
     */
    downloadFileName?: string;
}

export interface ExcelParseResult<T = Record<string, unknown>> {
    data: T[];
    sheetName: string;
    rowCount: number;
    columnCount: number;
    worksheet?: ExcelJS.Worksheet;
}

export interface AmazonComponent {
    type: string;
    id: string;
    fields: AmazonField[];
    children?: AmazonComponent[];
    columnIndex?: number;
}

export interface AmazonCanvasObject {
    type: string;
    id: string;
    fields: AmazonField[];
    columnIndex?: number;
}

export interface AmazonSurface {
    type: string;
    id: string;
    fields: AmazonField[];
}

export interface AmazonBinding {
    componentId: string;
    componentType: string;
    canvasObjectId: string;
    canvasObjectType: string;
}

export interface AmazonField {
    field: string;
    label: string;
    value: unknown;
}

export interface AmazonParsedResult {
    template: Record<string, unknown>;
    surfaces: AmazonSurface[];
    components: AmazonComponent[];
    canvasObjects: AmazonCanvasObject[];
    bindings: AmazonBinding[];
}

/**
 * Composable for parsing Amazon Custom Excel templates to Customily format
 */
export function useAmazonExcelParser() {
    /**
     * Parses an Excel file and returns the data as an array of objects
     */
    const parseExcelToJson = async <T = Record<string, unknown>>(
        buffer: ArrayBuffer,
        options: ExcelParseOptions = {}
    ): Promise<ExcelParseResult<T>> => {
        const { sheetIndex = 0, sheetName } = options;

        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);

        // Get the specified sheet
        let worksheet: ExcelJS.Worksheet | undefined;
        if (sheetName) {
            worksheet = workbook.worksheets.find((ws) => ws.name === sheetName);
        } else {
            worksheet = workbook.worksheets[sheetIndex];
        }

        if (!worksheet) {
            throw new Error(
                `Sheet ${
                    sheetName || `at index ${sheetIndex}`
                } not found in Excel file. Available sheets: ${workbook.worksheets.map((ws) => ws.name).join(', ')}`
            );
        }

        // Show info for ALL sheets
        workbook.worksheets.forEach((ws, idx) => {
            console.log(`  [${idx}] "${ws.name}": ${ws.rowCount} rows, ${ws.columnCount} cols`);
        });

        // Get all rows as array of arrays
        const rows: (ExcelJS.CellValue | undefined)[][] = [];
        worksheet.eachRow((row) => {
            rows.push(row.values as (ExcelJS.CellValue | undefined)[]);
        });

        if (rows.length === 0) {
            throw new Error('No data found in Excel file');
        }

        // Convert to objects using the first row as headers
        // Note: row.values[0] is empty in exceljs, data starts at index 1
        const headerRow = rows[0].slice(1);
        const data = rows.slice(1).map((row) => {
            const values = row.slice(1);
            const obj: Record<string, unknown> = {};
            headerRow.forEach((header, index) => {
                obj[String(header)] = values[index];
            });
            return obj as T;
        });

        return {
            data,
            sheetName: worksheet.name,
            rowCount: worksheet.rowCount,
            columnCount: worksheet.columnCount,
            worksheet
        };
    };

    /**
     * Parses Amazon Excel data into a Customily template structure
     * Based solely on row values, without using colors.
     *
     * Expected format:
     * - Row 1: Node identifier (ComponentName:ComponentId, can be empty)
     * - Row 2: Field name
     * - Row 3: Display label
     * - Row 4: Value
     *
     * Result Structure:
     * - template: Template metadata
     * - surfaces: Product surfaces
     * - components: Input/control components (*Component:id, dropdowns)
     * - canvasObjects: Visual objects (any Type:id that is not a component/surface/option)
     * - bindings: Component → CanvasObject relationships
     *
     * Node Classification:
     * 1. *Component:id → components (explicit, type ends in "Component")
     * 2. option:id → promotes preceding canvas object to DropdownComponent, then adds as child
     * 3. fontOption:id / colorOption:id → children of FontChooser/ColorChooser
     * 4. superficie/surface → surfaces
     * 5. Everything else Type:id → generic canvas object
     *
     * Binding Rules:
     * - Each canvas object binds to the last unbound component
     * - If no unbound component exists yet, queued and bound when next component appears
     */
    const parseAmazonFormat = (
        _rawData: Record<string, unknown>[],
        worksheet?: ExcelJS.Worksheet
    ): AmazonParsedResult => {
        if (!worksheet) {
            throw new Error('Worksheet is required for Amazon format parsing');
        }

        const nodeIdentifierRow = worksheet.getRow(1);
        const fieldNameRow = worksheet.getRow(2);
        const labelRow = worksheet.getRow(3);
        const valueRow = worksheet.getRow(4);

        const totalColumns = worksheet.columnCount;

        let isFirstNode = true; // Track if this is the first node (template name)

        // Helper: Explicit component (*Component:id)
        const isExplicitComponent = (nodeStr: string): boolean => {
            return /^[A-Za-z0-9]+Component:[A-Za-z0-9_-]+$/.test(nodeStr);
        };

        // Helper: Surface node
        const isSurface = (nodeStr: string): boolean => {
            const lowerNode = nodeStr.toLowerCase();
            return lowerNode.startsWith('superficie ') || lowerNode.startsWith('surface ');
        };

        const result: AmazonParsedResult = {
            template: {},
            surfaces: [],
            components: [],
            canvasObjects: [],
            bindings: []
        };

        let currentActiveNode: { fields: AmazonField[] } | null = null;
        let currentParentForChildren: AmazonComponent | null = null;
        let currentParentType = '';
        // Pending canvas objects waiting for a component to bind to
        const pendingCanvasObjects: AmazonCanvasObject[] = [];

        // Helper: Create binding between component and canvas object
        const createBinding = (component: AmazonComponent, canvasObject: AmazonCanvasObject) => {
            result.bindings.push({
                componentId: component.id,
                componentType: component.type,
                canvasObjectId: canvasObject.id,
                canvasObjectType: canvasObject.type
            });
        };

        // Helper: Bind next pending canvas object to component
        const bindPendingCanvasObjects = (component: AmazonComponent) => {
            if (pendingCanvasObjects.length > 0) {
                createBinding(component, pendingCanvasObjects.shift()!);
            }
        };

        for (let columnIndex = 1; columnIndex <= totalColumns; columnIndex++) {
            const nodeIdentifier = nodeIdentifierRow.getCell(columnIndex).value;
            const fieldName = fieldNameRow.getCell(columnIndex).value;
            const label = labelRow.getCell(columnIndex).value;
            const value = valueRow.getCell(columnIndex).value;

            const nodeIdentifierStr = nodeIdentifier ? String(nodeIdentifier).trim() : '';
            const fieldNameStr = fieldName ? String(fieldName).trim() : '';
            const labelStr = label ? String(label).trim() : '';

            // Skip "Seller Sku" column
            if (nodeIdentifierStr === 'Seller Sku') {
                continue;
            }

            // Case 0: First node is template name
            if (isFirstNode && nodeIdentifierStr) {
                const templateName = nodeIdentifierStr.includes(':')
                    ? nodeIdentifierStr.split(':', 2)[0]
                    : nodeIdentifierStr;
                result.template = { name: templateName };
                isFirstNode = false;
                continue;
            }

            isFirstNode = false;

            // Node classification — sets currentActiveNode, then falls through to field capture below
            if (nodeIdentifierStr && isSurface(nodeIdentifierStr)) {
                // Case 1: Surface node
                const [type, id] = nodeIdentifierStr.split(':', 2);
                const surface: AmazonSurface = { type, id, fields: [] };
                result.surfaces.push(surface);
                currentActiveNode = surface;
                currentParentForChildren = null;
                currentParentType = '';
            } else if (isExplicitComponent(nodeIdentifierStr)) {
                // Case 2: Explicit Component (*Component:id)
                const [type, id] = nodeIdentifierStr.split(':', 2);
                const component: AmazonComponent = { type, id, fields: [], columnIndex };
                const lowerType = type.toLowerCase();
                if (lowerType.includes('fontchooser') || lowerType.includes('colorchooser')) {
                    component.children = [];
                    currentParentForChildren = component;
                    currentParentType = lowerType.includes('fontchooser') ? 'fontchooser' : 'colorchooser';
                } else {
                    currentParentForChildren = null;
                    currentParentType = '';
                }
                result.components.push(component);
                currentActiveNode = component;
                bindPendingCanvasObjects(component);
            } else if (nodeIdentifierStr && nodeIdentifierStr.toLowerCase().startsWith('option:')) {
                // Case 3: option:id — first occurrence promotes preceding canvas object to DropdownComponent
                if (currentParentType !== 'dropdown' && currentParentForChildren === null) {
                    const lastCanvasIdx = result.canvasObjects.length - 1;
                    if (lastCanvasIdx >= 0) {
                        const promoted = result.canvasObjects.splice(lastCanvasIdx, 1)[0];
                        const dropdown: AmazonComponent = {
                            type: 'DropdownComponent',
                            id: promoted.id,
                            fields: promoted.fields,
                            children: [],
                            columnIndex: promoted.columnIndex
                        };
                        result.components.push(dropdown);
                        currentParentForChildren = dropdown;
                        currentParentType = 'dropdown';
                        currentActiveNode = dropdown;
                    }
                }
                const [type, id] = nodeIdentifierStr.split(':', 2);
                const option: AmazonComponent = { type, id, fields: [], columnIndex };
                if (currentParentForChildren && currentParentType === 'dropdown') {
                    if (!currentParentForChildren.children) currentParentForChildren.children = [];
                    currentParentForChildren.children.push(option);
                }
                currentActiveNode = option;
            } else if (nodeIdentifierStr && nodeIdentifierStr.toLowerCase().startsWith('fontoption:')) {
                // Case 4: fontOption:id (child of FontChooserComponent)
                const [type, id] = nodeIdentifierStr.split(':', 2);
                const fontOption: AmazonComponent = { type, id, fields: [], columnIndex };
                if (currentParentForChildren && currentParentType === 'fontchooser') {
                    if (!currentParentForChildren.children) currentParentForChildren.children = [];
                    currentParentForChildren.children.push(fontOption);
                }
                currentActiveNode = fontOption;
            } else if (nodeIdentifierStr && nodeIdentifierStr.toLowerCase().startsWith('coloroption:')) {
                // Case 5: colorOption:id (child of ColorChooserComponent)
                const [type, id] = nodeIdentifierStr.split(':', 2);
                const colorOption: AmazonComponent = { type, id, fields: [], columnIndex };
                if (currentParentForChildren && currentParentType === 'colorchooser') {
                    if (!currentParentForChildren.children) currentParentForChildren.children = [];
                    currentParentForChildren.children.push(colorOption);
                }
                currentActiveNode = colorOption;
            } else if (nodeIdentifierStr) {
                // Case 6: Generic canvas object — any Type:id not matched above
                const [type, id] = nodeIdentifierStr.split(':', 2);
                const canvasObject: AmazonCanvasObject = { type, id, fields: [], columnIndex };
                result.canvasObjects.push(canvasObject);
                currentActiveNode = canvasObject;
                currentParentForChildren = null;
                currentParentType = '';
                const lastUnbound = [...result.components]
                    .reverse()
                    .find((c) => !result.bindings.some((b) => b.componentId === c.id));
                if (lastUnbound) {
                    createBinding(lastUnbound, canvasObject);
                } else {
                    pendingCanvasObjects.push(canvasObject);
                }
            }

            // Capture field — runs for every column, including node-header columns that also carry a field
            if (fieldNameStr && currentActiveNode) {
                currentActiveNode.fields.push({ field: fieldNameStr, label: labelStr, value });
            }
        }

        return result;
    };

    return {
        parseExcelToJson,
        parseAmazonFormat
    };
}
